// The design-time API that components can use
import { findDesignerComponentClass } from "@designer/instantiation";
import {
    Component,
    ComponentConstructor,
    StringPropertyDescription,
    getDefaultDepIdForComponent,
} from "@runtime/components/Component";
import { SectionUpdates, designerApi, setDesignerApi } from "@runtime/runner/component-designer-api";
import {
    deleteSection,
    editState,
    persistentEditState,
    reloadSections,
    setDomNodeForSection,
    updateComponentDomNode,
} from "./edit-state";
import {
    deselectAll,
    notifySelectionChange,
    resendSelectedObjectBounds,
    resendSelectedObjectInteractions,
    setComponentPropertyValues,
    setSectionPropertyValues,
} from "./index";
import { BASE_MODULES, getLocalFormName } from "./python-environment";
import { NamedComponentUpdate, SectionUpdatesDTO, SlotTemplateUpdate } from "./shared-types";
import * as windowRpc from "./windowRpc";

// The IDE currently only understands bounds updates; anything else triggers a full reload.
// We drop property updates on the floor as the IDE can't handle them yet anyway.
const transformSectionUpdatesForTransfer = (sectionUpdates?: SectionUpdates) => {
    const updates: SectionUpdatesDTO = {};
    if (!sectionUpdates) {
        return null;
    }
    for (const [id, update] of Object.entries(sectionUpdates || {})) {
        if (!update || update.propertyDescriptions || update.interactions) {
            // We need a full update, abort
            return null;
        } else {
            updates[id] = {};
            if (update.element) {
                updates[id].bounds = update.element.getBoundingClientRect();
            }
            if (update.propertyValues) {
                updates[id].propertyValues = update.propertyValues;
            }
        }
    }
    return updates;
};

async function updateComponentSections(self: Component, sectionUpdates?: SectionUpdates) {
    const name = editState.namesByComponent.get(self);
    if (name === undefined) {
        return;
    }
    const sectionUpdatesForIde = transformSectionUpdatesForTransfer(sectionUpdates);
    if (sectionUpdatesForIde) {
        // Update the DOM element<->section cache.
        for (const [id, update] of Object.entries(sectionUpdates || {})) {
            if (!update) {
                deleteSection(name, id);
            } else if (update.element) {
                setDomNodeForSection(name, id, update.element);
            }
        }
        // Tell the IDE
        await windowRpc.call("updateComponentProperties", [
            { componentName: name, sectionUpdates: sectionUpdatesForIde } as NamedComponentUpdate,
        ]);
    } else {
        // Just reload everything
        reloadSections(name, self);
        await notifySelectionChange();
    }
}

async function updateComponentProperties(self: Component, propertyUpdates: object, layoutPropertyUpdates: object) {
    const name = editState.namesByComponent.get(self);
    const partialUpdate = { propertyUpdates, layoutPropertyUpdates };
    if (name !== undefined) {
        const layoutPropertyInfo = editState.layoutPropertyInfoByComponent.get(self);
        if (layoutPropertyInfo) {
            layoutPropertyInfo.layoutProperties = { ...layoutPropertyInfo.layoutProperties, ...layoutPropertyUpdates };
        }
        await windowRpc.call("updateComponentProperties", [
            { componentName: name, ...partialUpdate } as NamedComponentUpdate,
        ]);
    } else {
        const slot = editState.slotsByComponent.get(self);
        if (slot) {
            await windowRpc.call("updateComponentProperties", [
                { slotName: slot.name, ...partialUpdate } as SlotTemplateUpdate,
            ]);
        } else {
            throw new Error("This component is not on the currently editing form");
        }
    }
}

function startEditingSubform(subform: Component) {
    const formName = getLocalFormName(subform);
    if (formName && subform.anvil$designerPath) {
        console.log("Request that we start editing", subform.anvil$designerPath);
        deselectAll();
        windowRpc.call("requestChangeEditingPath", subform.anvil$designerPath, formName);
    }
}

function preEditSubform(subform: Component) {
    const formName = getLocalFormName(subform);
    if (formName && subform.anvil$designerPath) {
        console.log("Pre Request for editing", subform.anvil$designerPath);
        windowRpc.call("preRequestChangeEditingPath", subform.anvil$designerPath, formName);
    }
}

const INPUT_NAMES = ["INPUT", "TEXTAREA"];

function startInlineEditing(
    pyComponent: Component,
    prop: StringPropertyDescription,
    element: HTMLElement,
    { onFinished, sectionId = null }: { onFinished?: () => void; sectionId?: string | null } = {}
) {
    console.log("Inline edit", element);

    editState.inlineEditing = element;
    const isInputElement = INPUT_NAMES.includes(element.nodeName);
    const initialText = isInputElement ? (element as HTMLInputElement).value : element.textContent;
    const wasDisabled = element.getAttribute("disabled");
    if (wasDisabled !== null) {
        element.removeAttribute("disabled");
    }
    if (!isInputElement) {
        element.contentEditable = "true";
    }
    const oldStyles = {
        whiteSpace: element.style.whiteSpace,
        cursor: element.style.cursor,
        paddingLeft: element.style.paddingLeft,
    };
    if (window.getComputedStyle(element).paddingLeft === "0px") {
        // fixes can't see a cursor on empty span elements
        element.style.paddingLeft = "1px";
    }
    element.style.whiteSpace = "pre-wrap";
    element.style.setProperty("cursor", "text", "important");
    element.focus();

    if (isInputElement) {
        (element as HTMLInputElement).select();
    } else {
        const range = document.createRange();
        range.selectNodeContents(element);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
    }

    const onKeydown = (e: KeyboardEvent) => {
        // don't allow the element to handle keydown events
        // e.g. elements that do something on the space key down event to toggle a checkbox etc
        // don't prevent the default otherwise we break things
        // any defaultPrevented behavior typically leads to bugs
        // e.g. https://anvil.works/forum/t/fixed-richtext-editing-bug-in-new-designer/19250
        // the above post was the direct result of overriding space and enter behavior
        // and with these overrides, the edit history was broken, so the browser couldn't undo/redo
        e.stopImmediatePropagation();
        // Selected objects have a resize observer so we don't need to resend selectedObjectBounds on keychange
        // console.log("KEYDOWN", "'" + e.key + "'")
        let exit = false;

        switch (e.key) {
            case "Tab":
                exit = true;
                // TODO: Probably want to edit the next inline-editable component.
                break;
            case "Escape":
                exit = true;
                if (isInputElement) {
                    (element as HTMLInputElement).value = initialText ?? "";
                } else {
                    element.textContent = initialText;
                }
                break;
            case "Enter":
                exit = !(prop.multiline && e.shiftKey);
                break;
        }
        if (exit) {
            e.preventDefault();
            element.blur();
        }
    };
    const onBlur = async () => {
        element.removeEventListener("blur", onBlur, true);
        document.removeEventListener("keydown", onKeydown, true);

        delete editState.inlineEditing;
        window.getSelection()?.removeAllRanges();
        if (!isInputElement) {
            element.contentEditable = "false";
        }
        Object.assign(element.style, oldStyles);
        if (wasDisabled !== null) {
            element.setAttribute("disabled", wasDisabled);
        }
        const newText = isInputElement ? (element as HTMLInputElement).value : element.textContent;
        console.log("Initial", initialText, "New Text", newText);

        onFinished?.();

        if (newText === initialText) return;

        const name = editState.namesByComponent.get(pyComponent)!;
        if (!sectionId) {
            const updates = await setComponentPropertyValues(name, { [prop.name]: newText });
            designerApi.updateComponentProperties(pyComponent, updates, {});
        } else {
            const sectionUpdates = await setSectionPropertyValues(name, sectionId, { [prop.name]: newText });
            designerApi.updateComponentSections(pyComponent, { [sectionId]: { propertyValues: sectionUpdates! } });
            // TODO: use updateComponentProperties to send sectionUpdates for this component. Then make the designer understand more than just updated bounds.
        }
        resendSelectedObjectBounds();
    };
    element.addEventListener("blur", onBlur, true);
    document.addEventListener("keydown", onKeydown, true);
}

function startEditingForm(requestingComponent: Component, componentId: string) {
    const [_, depId, formName] = componentId.match(/^(?:([^:]+):)?([^:]+)$/) ?? [];
    if (formName) {
        windowRpc.raiseEvent(
            "requestEditForm",
            depId || getDefaultDepIdForComponent(requestingComponent) || undefined,
            formName
        );
    }
}

function registerInteraction(
    pyComponent: Component,
    element: Element,
    event: string,
    callback: (e: MouseEvent) => void
) {
    if (event === "dblclick") {
        element.setAttribute("data-anvil-selectable", "true");
        editState.designInteractionsByDomNode.set(element, {
            component: pyComponent,
            callbacks: { dblclick: callback },
        });
    }
}

function notifyInteractionsChanged(pyComponent: Component) {
    // For now, we don't care what changed, just send updates for everything the IDE designer knows about.
    resendSelectedObjectInteractions();
}

function notifyDomNodeChanged(pyComponent: Component) {
    updateComponentDomNode(pyComponent);
}

function getDesignerState(pyComponent: Component) {
    const path = pyComponent.anvil$designerPath?.join("|");
    if (path) {
        if (!persistentEditState.designerState.has(path)) {
            console.log("Adding component state", path);
            persistentEditState.designerState.set(path, new Map());
        }
        return persistentEditState.designerState.get(path)!;
    } else {
        console.log("Returning fake component state for", pyComponent);
        return new Map();
    }
}

function getDesignComponent(pyComponentClass: ComponentConstructor) {
    return findDesignerComponentClass(BASE_MODULES["anvil"], pyComponentClass);
}

function requestFormPropertyChange(
    pyRequestingComponent: Component,
    formProperty: string | ComponentConstructor,
    propertyName: string,
    newValue: any
) {
    if (
        typeof formProperty === "string" &&
        formProperty.indexOf(":") === -1 &&
        getDefaultDepIdForComponent(pyRequestingComponent) === null
    ) {
        windowRpc.call("requestSetFormProperty", formProperty, propertyName, newValue);
    }
}

function getDesignName(pyComponent: Component) {
    return editState.namesByComponent.get(pyComponent) || null;
}

setDesignerApi({
    inDesigner: true,
    updateComponentProperties,
    updateComponentSections,
    preEditSubform,
    startEditingSubform,
    startEditingForm,
    registerInteraction,
    startInlineEditing,
    notifyInteractionsChanged,
    notifyDomNodeChanged,
    getDesignerState,
    getDesignComponent,
    requestFormPropertyChange,
    getDesignName,
});
